<script setup lang="ts">
import { useGetProjects } from "@/stores/queries";
import { trackAppFeature } from "@/analytics/app-analytics";
import { trackDebounced } from "@/analytics/track-debounce";
import { computed, ref, watch } from "vue";
import ProjectItem from "@/components/projects/ProjectItem.vue";
import {
  ProjectStatus,
  type DRPartialProject,
  type Project,
} from "@/types/projects.types";
import type { PaginationParams } from "@/types";
import { useDeviceState } from "@/stores/DeviceState";
import { storeToRefs } from "pinia";
import DebounceInput from "@/components/UI/form/DebounceInput.vue";
import { useScreen } from "@/composable/useResponsive";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import CreateProjectBtn from "@/components/projects/CreateProjectBtn.vue";
import DRDisplayList from "@/components/UI/items/DRDisplayList.vue";

const defaultStatusFilter = [ProjectStatus.IN_PROGRESS, ProjectStatus.READY];
const compateArray = (arr1: any[], arr2: any[]) => {
  if (arr1.length !== arr2.length) return false;
  for (const item of arr1) {
    if (!arr2.includes(item)) return false;
  }
  return true;
};
const { screen } = useScreen();
const deviceState = useDeviceState();
const { isOnline } = storeToRefs(deviceState);

const page = ref(1);
const rowsPerPage = 12;
const sortBy = ref<"createdAt" | "name" | "status">("createdAt");
const sortByOptions: string[] = ["createdAt", "name", "status"];
const statusFilter = ref<ProjectStatus[]>(defaultStatusFilter);
const statusList = Object.values(ProjectStatus);
const query = ref("");
const isFilters = computed(() => {
  return (
    !!query.value?.length ||
    !compateArray(statusFilter.value, defaultStatusFilter)
  );
});

const removeFilters = () => {
  query.value = "";
  statusFilter.value = defaultStatusFilter;
};

const pagination = computed<PaginationParams>(() => ({
  skip: (page.value - 1) * rowsPerPage,
  take: rowsPerPage,
  sortBy: sortBy.value,
  order: sortBy.value === "createdAt" ? "desc" : "asc",
}));

// Typing is debounced into one event per search; the status filter tracks on
// change. Query text stays out of the event (no business content).
watch(query, (value) => {
  if (!value) return;
  trackDebounced("projects_search", () =>
    trackAppFeature("search_used", { page: "projects" }),
  );
});
watch(statusFilter, (value) => {
  if (!compateArray(value, defaultStatusFilter)) {
    trackAppFeature("filters_used", { page: "projects" });
  }
});

const filters = computed(() => {
  const filters: Record<string, any> = {};
  if (statusFilter.value) {
    filters.status = statusFilter.value;
  }
  if (query.value) {
    filters.search = query.value;
  }
  return filters;
});

const { data, localData, isLoading } = useGetProjects(pagination, filters);

const projects = computed<(Project | DRPartialProject)[]>(() => {
  return data.value?.projects ?? localData.value ?? [];
});
const totalCount = computed<number>(() => {
  return data.value?.totalCount ?? 0;
});
</script>

<template>
  <div class="my-5 mx-3 mx-md-4 mx-lg-5 px-sm-2 px-md-4 px-lg-5">
    <div class="d-flex align-items-center gap-3">
      <div class="h2">{{ $t("dashboard:projects.list.title") }}</div>
      <div class="flex-grow-1"></div>
      <CreateProjectBtn />
    </div>

    <div
      class="d-flex justify-content-between text-end my-2 py-2 gap-2"
      :class="!screen.gt.sm && 'w-100 flex-column'"
    >
      <div
        class="d-flex gap-2 align-items-center"
        :class="!screen.gt.sm && 'flex-column'"
      >
        <DebounceInput
          v-model="query"
          :placeholder="$t('search')"
          :debounce="500"
          :style="{ width: screen.gt.sm ? '200px' : '100%' }"
          :size="screen.gt.sm ? 'large' : 'default'"
          search
        />

        <el-select
          v-model="statusFilter"
          multiple
          collapse-tags
          collapse-tags-tooltip
          :placeholder="$t('dashboard:projects.list.filters.status')"
          :style="{
            width: screen.gt.sm ? '200px' : '100%',
          }"
          :size="screen.gt.sm ? 'large' : 'default'"
        >
          <el-option
            v-for="status in statusList"
            :key="status"
            :label="$t('dashboard:projects.status.label.' + status)"
            :value="status"
          >
          </el-option>

          <template #prefix>
            <el-icon size="1rem"> <i class="bi-funnel"></i></el-icon>
          </template>
        </el-select>
        <DRBtnIcon
          v-if="screen.gt.sm && isFilters"
          variant="flat-danger"
          icon="bi-arrow-counterclockwise"
          :size="40"
          @click="removeFilters"
        >
        </DRBtnIcon>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <el-select
          v-model="sortBy"
          placeholder="Trier par"
          :style="{
            width: screen.gt.sm ? '200px' : '100%',
          }"
          :size="screen.gt.sm ? 'large' : 'default'"
        >
          <el-option
            v-for="key in sortByOptions"
            :key="key"
            :label="$t('dashboard:projects.list.sortBy.' + key)"
            :value="key"
          >
          </el-option>

          <template #prefix>
            <el-icon size="1.5rem"> <i class="bi-sort-alpha-down"></i></el-icon>
          </template>
        </el-select>
      </div>
      <DRBtn
        v-if="!screen.gt.sm && isFilters"
        variant="outline-danger"
        :label="$t('clear-filters')"
        @click="removeFilters"
      >
      </DRBtn>
    </div>

    <div class="pt-2">
      <DRDisplayList
        :data="projects"
        :loading="isLoading"
        key-index="id"
        :empty-message="$t('dashboard:projects.list.empty')"
        :min-width="250"
      >
        <template #item="{ item, display }">
          <ProjectItem
            :project="item"
            :readonly="!isOnline"
            :display="display"
          />
        </template>
      </DRDisplayList>
    </div>

    <div
      v-if="totalCount > rowsPerPage"
      class="d-flex justify-content-center mt-4"
    >
      <el-pagination
        v-model:current-page="page"
        :page-size="rowsPerPage"
        background
        layout="prev, pager, next"
        :total="totalCount"
      />
    </div>
  </div>
</template>

<style lang="scss">
@import "@/style/variables.scss";
.dr-input {
  max-width: 300px;
}

.el-radio-button__inner {
  background-color: transparent !important;
  color: #80808097 !important;
  padding: 0 !important;
  width: 40px !important;
  height: 40px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  border-color: inherit;
}

.el-radio-button.is-active .el-radio-button__inner {
  background-color: white !important;
  color: $dark !important;
  border-color: #bebebe9d !important;
  box-shadow: none !important;
}
</style>
